import {
  faChevronDown,
  faChevronRight,
  faCircleQuestion
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Checkbox,
  FormControl,
  Grid,
  InputLabel,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import { useAtom } from 'jotai';
import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import JsonInput from '@/components/JsonInput';
import TimezoneSelector from '@/components/TimezoneSelector';
import theme from '@/theme';

import { logSourcesAtom } from './LogSourcesCreate.store';
import type {
  DataStreamModel,
  LogSourceModel,
  LogSourceProps
} from './LogSourcesCreate.types';
import useStyles from './LogSourcesCreateRow.styles';

const LogSourceRow = (props: LogSourceProps) => {
  const { row } = props;
  const [open, setOpen] = useState(false);
  const { register, setValue } = useFormContext();
  const [, setLogSources] = useAtom(logSourcesAtom);

  const { classes } = useStyles();

  const handleCheckboxChange = (
    productName: string,
    streamName: string | null = null
  ) => {
    setLogSources((prevSelections) => {
      const newSelections: LogSourceModel[] = [...prevSelections];

      // Find the parent product by productName
      const parentIndex = newSelections.findIndex(
        (product) => product.name === productName
      );
      if (parentIndex === -1) {
        console.error(`Product with name ${productName} not found.`);
        return prevSelections; // Return the current state if product not found
      }

      if (streamName === null) {
        // Toggle the main product checkbox
        const wasSelected = newSelections[parentIndex].isSelected;
        const nowSelected = !wasSelected;

        newSelections[parentIndex].isSelected = nowSelected;
        newSelections[parentIndex].selectionSource = nowSelected
          ? 'user-selected'
          : 'user-deselected';

        // If data streams exist, toggle their checkboxes too
        if (newSelections[parentIndex].data_streams) {
          newSelections[parentIndex].data_streams = newSelections[
            parentIndex
          ].data_streams.map((stream) => ({
            ...stream,
            isSelected: nowSelected,
            selectionSource: nowSelected ? 'user-selected' : 'user-deselected'
          })) as DataStreamModel[];
        }
        newSelections[parentIndex].streamsPartlySelected = false;
      } else if (newSelections[parentIndex]?.data_streams) {
        // Find the specific data stream by streamName
        const streamIndex = newSelections[parentIndex].data_streams.findIndex(
          (stream) => stream.name === streamName
        );

        if (streamIndex === -1) {
          console.error(
            `Stream with name ${streamName} not found in product ${productName}.`
          );
          return prevSelections; // Return the current state if stream not found
        }

        // Toggle the specific stream
        newSelections[parentIndex].data_streams = newSelections[
          parentIndex
        ].data_streams.map((stream: DataStreamModel, index) => {
          if (index === streamIndex) {
            const streamSelected = !stream.isSelected;
            return {
              ...stream,
              isSelected: streamSelected,
              selectionSource: streamSelected
                ? 'user-selected'
                : 'user-deselected'
            };
          }
          return stream;
        });

        // Calculate parent state based on children
        const allStreamsSelected = newSelections[
          parentIndex
        ].data_streams.every((stream: DataStreamModel) => stream.isSelected);
        const someStreamsSelected = newSelections[
          parentIndex
        ].data_streams.some((stream: DataStreamModel) => stream.isSelected);
        const noStreamsSelected = !someStreamsSelected;

        // Detect ANY user interaction with streams
        const hasUserInteraction = newSelections[parentIndex].data_streams.some(
          (stream: DataStreamModel) =>
            stream.selectionSource === 'user-selected' ||
            stream.selectionSource === 'user-deselected'
        );

        // Update parent selection state
        newSelections[parentIndex].isSelected = allStreamsSelected;
        newSelections[parentIndex].streamsPartlySelected =
          !allStreamsSelected && someStreamsSelected;

        // Parent selectionSource logic
        if (allStreamsSelected && hasUserInteraction) {
          // All streams selected AND user touched ANY stream → save as wildcard
          newSelections[parentIndex].selectionSource = 'user-selected';
        } else if (noStreamsSelected) {
          // No streams selected → mark parent as deselected
          newSelections[parentIndex].selectionSource = 'user-deselected';
        } else if (someStreamsSelected && hasUserInteraction) {
          // Partial selection with user interaction → keep individual streams
          newSelections[parentIndex].selectionSource = 'user-deselected';
        } else {
          // No user interaction → preserve backend state
          newSelections[parentIndex].selectionSource = 'unchanged';
        }
      }

      return newSelections;
    });
  };

  const descriptionElementRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (open) {
      const { current: descriptionElement } = descriptionElementRef;
      if (descriptionElement !== null) {
        descriptionElement.focus();
      }
    }
  }, [open]);

  const handleTimeZoneChange = (value: string, streamName: string) => {
    if (value && value !== 'Select Timezone') {
      const configJson = { timezone: value };
      const configKey = `${streamName}_configuration`;
      setValue(`${configKey}`, JSON.stringify(configJson));
      row.timeZone = value ?? '';
    } else {
      const configKey = `${streamName}_configuration`;
      setValue(`${configKey}`, '');
    }
  };

  return (
    <>
      <TableRow key={row.name} sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell
          component="th"
          style={{ width: '50%', padding: '.5rem', cursor: 'pointer' }}
          onClick={() => setOpen(!open)}
        >
          <IconButton aria-label="expand row" size="small">
            {open ? (
              <FontAwesomeIcon
                icon={faChevronRight}
                style={{ fontSize: '0.9rem' }}
              />
            ) : (
              <FontAwesomeIcon
                icon={faChevronDown}
                style={{ fontSize: '0.9rem' }}
              />
            )}
          </IconButton>
          {row.display_name}
        </TableCell>
        <TableCell component="th" scope="row" style={{ padding: '.5rem' }}>
          {row.doc_link ? (
            <Tooltip
              arrow
              classes={{
                arrow: classes.helpTooltipArrow,
                tooltip: classes.helpTooltip
              }}
              title="Help"
            >
              <IconButton
                aria-label="Open documentation"
                component="a"
                style={{ color: theme.vars.palette.primary.main }}
                href={row.doc_link}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FontAwesomeIcon size="xs" icon={faCircleQuestion} />
              </IconButton>
            </Tooltip>
          ) : null}
        </TableCell>
        <TableCell component="th" scope="row" style={{ padding: '.5rem' }}>
          {row.product_type}
        </TableCell>
        <TableCell
          component="th"
          scope="row"
          style={{ padding: '.5rem', textAlign: 'right' }}
        >
          <Typography variant="body2" component="span" color="textSecondary">
            {row.type}
          </Typography>
          <Checkbox
            key={row.name}
            size="small"
            sx={{ float: 'right', padding: '0', ml: 1 }}
            checked={row.isSelected || false}
            indeterminate={row?.streamsPartlySelected || false}
            onChange={() => handleCheckboxChange(row.name)}
          />
        </TableCell>
      </TableRow>
      <TableRow key={`${row.name}-streams`}>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            {row?.data_streams !== undefined &&
              row?.data_streams !== null &&
              row.data_streams?.length > 0 && (
                <Box
                  key={`${row.name}-streams-box1`}
                  sx={{ flexGrow: 1 }}
                  style={{ paddingBottom: '1.25rem' }}
                >
                  <Grid key={`${row.name}-streams-grid1`} container spacing={2}>
                    <Grid
                      key={`${row.name}-streams-grid2`}
                      size={{ xs: 12, sm: 6, md: 6 }}
                    >
                      <InputLabel htmlFor="device-name-input">
                        Streams
                      </InputLabel>
                      <Box
                        className={classes.streamConfigBox}
                        key={`${row.name}-streams-box2`}
                      >
                        {row.data_streams?.map((stream: DataStreamModel) => (
                          <Stack
                            key={`${stream?.name}-box1`}
                            direction="row"
                            style={{
                              padding: '.5rem',
                              alignItems: 'center',
                              gap: 1
                            }}
                          >
                            <Box
                              key={`${stream?.name}-box2`}
                              component="span"
                              sx={{ flexGrow: 1 }}
                            >
                              {stream?.display_name || ''}
                            </Box>
                            <Typography variant="body2" color="textSecondary">
                              {stream.type}
                            </Typography>
                            <Checkbox
                              key={stream?.name}
                              size="small"
                              style={{ float: 'right', padding: '0' }}
                              checked={stream?.isSelected || false}
                              onChange={() =>
                                handleCheckboxChange(row.name, stream?.name)
                              }
                            />
                          </Stack>
                        ))}
                      </Box>
                    </Grid>

                    <Grid
                      key={`${row.name}-streams-grid3`}
                      size={{ xs: 12, sm: 6, md: 6 }}
                    >
                      <InputLabel htmlFor="device-name-input">
                        Source Configuration
                      </InputLabel>
                      <FormControl fullWidth>
                        <TimezoneSelector
                          name={row.name}
                          value={row.timeZone || ''}
                          onChange={(newValue, name) =>
                            handleTimeZoneChange(newValue ?? '', name)
                          }
                          register={register}
                        />

                        <JsonInput
                          {...register(`${row.name}_configuration`)}
                          rows={6}
                          name={`${row.name}_configuration`}
                          label="Source Configuration"
                        />
                      </FormControl>
                    </Grid>
                  </Grid>
                </Box>
              )}

            {(row?.data_streams === undefined ||
              row?.data_streams === null ||
              row?.data_streams?.length === 0) && (
              <Grid
                key={`${row.name}-grid4`}
                size={{ xs: 12, sm: 6, md: 6 }}
                style={{ paddingBottom: '1.25rem' }}
              >
                <InputLabel htmlFor="device-name-input">
                  Source Configuration
                </InputLabel>
                <FormControl fullWidth>
                  <TimezoneSelector
                    name={row.name}
                    value={row.timeZone || ''}
                    onChange={(newValue, name) =>
                      handleTimeZoneChange(newValue ?? '', name)
                    }
                    register={register}
                  />
                </FormControl>

                <FormControl fullWidth>
                  <JsonInput
                    {...register(`${row.name}_configuration`)}
                    rows={2}
                    name={`${row.name}_configuration`}
                    label="Source Configuration"
                  />
                </FormControl>
              </Grid>
            )}
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};
export default LogSourceRow;
